import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import ExcelJS from "exceljs";
import { jsPDF } from "jspdf";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import Dropdown from "../../common/dropDown";
import ReportTable from "../../common/ReportTable/ReportTable";
import { useUserData } from "../../../hooks/useUserData";
import { canViewIsfReports } from "./isfPermissionUtils";
import utils from "../../../utils/utils";
import {
  fetchEisfStudies,
  fetchStudyFolderBundle,
  getCachedEisfStudies,
} from "./isfDashboardService";
import {
  computeFolderReadiness,
  computePlaceholderReadiness,
  decorateReadinessReportSignatures,
  enrichDocumentsWithPlaceholderLinks,
  fetchReadinessReport,
  fetchStudyPlaceholdersForGates,
  filterReadinessReportByGate,
} from "./isfPlaceholderService";
import {
  buildSiteSelectOptions,
  fetchStudySitesForSetup,
  getAllowedStudySites,
  normalizeStudySiteRows,
} from "./isfSiteSetupService";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import IsfPhaseFilter from "./IsfPhaseFilter";
import StudyGateProgressStrip from "./StudyGateProgressStrip";
import useStudyGateView from "./useStudyGateView";
import "./IsfReports.scss";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const ALL_SITES_OPTION = { label: "All sites", value: "" };

const REPORT_VIEWS = [
  { id: "readiness", label: "Inspection readiness" },
  { id: "missing", label: "Missing required" },
  { id: "at-risk", label: "Overdue & at-risk" },
  { id: "sites", label: "Site comparison" },
];

const RISK_FILTER_OPTIONS = [
  { label: "All buckets", value: "" },
  { label: "Overdue", value: "Overdue" },
  { label: "Due within 7 days", value: "Due within 7 days" },
  { label: "Due within 14 days", value: "Due within 14 days" },
  { label: "Due within 30 days", value: "Due within 30 days" },
  { label: "No due date", value: "No due date" },
];

const DEFAULT_CATEGORY = { label: "eISF", value: "eISF" };

function formatReportDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
}

function formatSignatureStatus(row) {
  const state = String(row?.SignatureState || "").toLowerCase();
  if (state === "signed") return "Signed";
  if (state === "await") return "Awaiting signature";
  if (
    row?.RequiresSignature === 1 ||
    row?.RequiresSignature === true ||
    row?.RequiresSignature === "1"
  ) {
    return "Awaiting signature";
  }
  return "Not required";
}

function resolveSiteSelection(pool, currentSite) {
  if (!pool.length) return ALL_SITES_OPTION;
  const siteIds = new Set(pool.map((row) => row.SiteId).filter(Boolean));
  if (currentSite?.value && siteIds.has(currentSite.value)) {
    return currentSite;
  }
  return ALL_SITES_OPTION;
}

function applyReportPayload(data, setters) {
  const ph = computePlaceholderReadiness(data.summary);
  setters.setSummary(data.summary);
  setters.setPlaceholderDetails(data.placeholderDetails);
  setters.setMilestoneSummary(data.milestoneSummary);
  setters.setFolderCompleteness(data.folderCompleteness);
  setters.setAtRiskDetail(data.atRiskDetail);
  setters.setSiteComparison(data.siteComparison);
  if (ph.mode === "placeholder") {
    setters.setReadiness(ph);
  } else {
    const folder = computeFolderReadiness(data.folderCompleteness);
    setters.setReadiness({ mode: "folder", ...folder });
  }
}

function ReadinessRing({ percent = 0, label }) {
  const radius = 54;
  const circumference = 2 * Math.PI * radius;
  const safe = Math.min(100, Math.max(0, Number(percent) || 0));
  const offset = circumference - (safe / 100) * circumference;

  return (
    <div className="isf-reports-hub__ring">
      <div className="isf-reports-hub__ring-chart">
        <svg className="isf-reports-hub__ring-svg" viewBox="0 0 120 120" aria-hidden>
          <circle className="isf-reports-hub__ring-track" cx="60" cy="60" r={radius} />
          <circle
            className="isf-reports-hub__ring-progress"
            cx="60"
            cy="60"
            r={radius}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
          />
        </svg>
        <div className="isf-reports-hub__ring-value">{safe}%</div>
      </div>
      {label ? <p className="isf-reports-hub__ring-label">{label}</p> : null}
    </div>
  );
}

const IsfReports = () => {
  const { userData, loading } = useUserData();
  const reportRunRef = useRef(0);
  const studyCategoryKeyRef = useRef("");
  const lastFetchedKeyRef = useRef("");
  const inFlightFetchKeyRef = useRef("");
  const canView = userData && canViewIsfReports(userData);

  const [studies, setStudies] = useState([]);
  const [studiesLoading, setStudiesLoading] = useState(true);
  const [study, setStudy] = useState(null);
  const [category, setCategory] = useState(DEFAULT_CATEGORY);
  const [site, setSite] = useState(ALL_SITES_OPTION);
  const [siteRows, setSiteRows] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);
  const [reportView, setReportView] = useState("readiness");
  const [riskFilter, setRiskFilter] = useState(RISK_FILTER_OPTIONS[0]);
  const [loadError, setLoadError] = useState("");

  const [summary, setSummary] = useState(null);
  const [placeholderDetails, setPlaceholderDetails] = useState([]);
  const [milestoneSummary, setMilestoneSummary] = useState([]);
  const [folderCompleteness, setFolderCompleteness] = useState([]);
  const [atRiskDetail, setAtRiskDetail] = useState([]);
  const [siteComparison, setSiteComparison] = useState([]);
  const [readiness, setReadiness] = useState({ mode: "folder", percent: 0 });
  const [rawReport, setRawReport] = useState(null);

  const {
    studyGate,
    studyGateList,
    studyGateProgress,
    selectedGateCodes,
    setSelectedGateCodes,
    effectiveGateCodes,
    phaseLabel,
    gateLoading,
  } = useStudyGateView({
    userData,
    studyId: study?.value,
  });

  const categoryOptions = [
    { label: "All", value: "" },
    { label: "eISF", value: "eISF" },
    { label: "ePSF", value: "ePSF" },
  ];

  const loadStudies = useCallback(async () => {
    if (!userData?.OrganizationId) {
      setStudiesLoading(false);
      return;
    }

    const cached = getCachedEisfStudies(userData.OrganizationId, userData.StaffId);
    if (cached) {
      setStudies(cached.studies);
      setStudy((prev) => {
        if (prev?.value && cached.options.some((o) => o.value === prev.value)) return prev;
        return null;
      });
      setStudiesLoading(false);
      return;
    }

    setStudiesLoading(true);
    try {
      const { studies: list, options } = await fetchEisfStudies({
        organizationId: userData.OrganizationId,
        createdBy: userData.StaffId,
      });
      setStudies(list);
      setStudy((prev) => {
        if (prev?.value && options.some((o) => o.value === prev.value)) return prev;
        return null;
      });
      if (!options.length) {
        setLoadError("No studies found for your account. Check study access or try the main eISF page.");
      }
    } catch (err) {
      console.error("Error loading studies for reports:", err);
      setStudies([]);
      setStudy(null);
      setLoadError("Could not load studies. Confirm the API is running and you are logged in.");
    } finally {
      setStudiesLoading(false);
    }
  }, [userData]);

  const refreshReport = useCallback(() => {
    lastFetchedKeyRef.current = "";
    inFlightFetchKeyRef.current = "";
    setRefreshToken((token) => token + 1);
  }, []);

  useEffect(() => {
    loadStudies();
  }, [loadStudies]);

  useEffect(() => {
    if (!userData?.OrganizationId || !study?.value) {
      setSiteRows([]);
      setSitesLoading(false);
      setReportLoading(false);
      lastFetchedKeyRef.current = "";
      inFlightFetchKeyRef.current = "";
      return;
    }

    const runId = ++reportRunRef.current;
    const studyCategoryKey = `${study.value}|${category?.value || ""}`;
    const studyCategoryChanged = studyCategoryKeyRef.current !== studyCategoryKey;
    studyCategoryKeyRef.current = studyCategoryKey;

    let cancelled = false;

    (async () => {
      setReportLoading(true);
      setLoadError("");

      let siteForReport = site;
      let fetchKey = "";

      try {
        if (studyCategoryChanged) {
          siteForReport = ALL_SITES_OPTION;
          setSite(ALL_SITES_OPTION);
          setSitesLoading(true);
          const templateCategory = category?.value || null;
          let response = await fetchStudySitesForSetup({
            organizationId: userData.OrganizationId,
            studyId: study.value,
            templateCategory,
            createdBy: userData.StaffId,
          });
          let rows = normalizeStudySiteRows(response);
          if (!rows.length && templateCategory) {
            response = await fetchStudySitesForSetup({
              organizationId: userData.OrganizationId,
              studyId: study.value,
              templateCategory: null,
              createdBy: userData.StaffId,
            });
            rows = normalizeStudySiteRows(response);
          }
          if (cancelled || runId !== reportRunRef.current) return;

          const allowed = getAllowedStudySites(rows, userData);
          const pool = allowed.length ? allowed : rows;
          siteForReport = resolveSiteSelection(pool, site);
          setSiteRows(pool);
          if (siteForReport?.value !== site?.value || siteForReport?.label !== site?.label) {
            setSite(siteForReport);
          }
          setSitesLoading(false);
        }

        fetchKey = `${study.value}|${category?.value || ""}|${siteForReport?.value || ""}|${refreshToken}`;
        if (lastFetchedKeyRef.current === fetchKey) {
          setReportLoading(false);
          setSitesLoading(false);
          return;
        }
        if (inFlightFetchKeyRef.current === fetchKey) {
          return;
        }
        inFlightFetchKeyRef.current = fetchKey;

        const data = await fetchReadinessReport({
          organizationId: userData.OrganizationId,
          studyId: study.value,
          templateCategory: category?.value || null,
          createdBy: userData.StaffId,
          siteIdRef: siteForReport?.value || null,
        });

        if (cancelled || runId !== reportRunRef.current) return;

        lastFetchedKeyRef.current = fetchKey;
        setRawReport(data);
      } catch (err) {
        if (cancelled || runId !== reportRunRef.current) return;
        console.error("Error loading readiness report:", err);
        setLoadError("Report data could not be loaded for the selected study.");
        setRawReport(null);
        applyReportPayload(
          {
            summary: null,
            placeholderDetails: [],
            milestoneSummary: [],
            folderCompleteness: [],
            atRiskDetail: [],
            siteComparison: [],
          },
          {
            setSummary,
            setPlaceholderDetails,
            setMilestoneSummary,
            setFolderCompleteness,
            setAtRiskDetail,
            setSiteComparison,
            setReadiness,
          },
        );
      } finally {
        if (inFlightFetchKeyRef.current === fetchKey) {
          inFlightFetchKeyRef.current = "";
        }
        if (!cancelled && runId === reportRunRef.current) {
          setReportLoading(false);
          setSitesLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    userData,
    study?.value,
    study?.label,
    category?.value,
    site?.value,
    site?.label,
    refreshToken,
  ]);

  useEffect(() => {
    if (!rawReport || !userData?.OrganizationId || !study?.value) return;

    let cancelled = false;

    (async () => {
      const [placeholders, folderBundle] = await Promise.all([
        fetchStudyPlaceholdersForGates({
          organizationId: userData.OrganizationId,
          studyId: study.value,
          templateCategory: category?.value || null,
          createdBy: userData.StaffId,
          gateCodes: effectiveGateCodes,
        }).catch(() => []),
        fetchStudyFolderBundle({
          organizationId: userData.OrganizationId,
          staffId: userData.StaffId,
          studyId: study.value,
          templateCategory: category?.value || null,
        }).catch(() => ({ staffList: [], documents: [] })),
      ]);

      if (cancelled) return;

      const scoped =
        effectiveGateCodes.length > 0
          ? filterReadinessReportByGate(rawReport, placeholders)
          : rawReport;
      const documents = enrichDocumentsWithPlaceholderLinks(
        folderBundle.documents,
        placeholders,
      );
      const withSignatures = decorateReadinessReportSignatures(
        scoped,
        placeholders,
        documents,
      );

      applyReportPayload(withSignatures, {
        setSummary,
        setPlaceholderDetails,
        setMilestoneSummary,
        setFolderCompleteness,
        setAtRiskDetail,
        setSiteComparison,
        setReadiness,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [
    rawReport,
    effectiveGateCodes,
    userData,
    study?.value,
    category?.value,
  ]);

  const studyOptions = useMemo(
    () => utils.mapToOptions(studies, "StudyName", "StudyId"),
    [studies],
  );

  const siteOptions = useMemo(
    () => [ALL_SITES_OPTION, ...buildSiteSelectOptions(siteRows)],
    [siteRows],
  );

  const showSiteColumn = !site?.value && siteRows.length > 1;

  const readinessFieldOptions = useMemo(
    () => [
      ...(showSiteColumn ? [{ key: "siteName", label: "Site" }] : []),
      { key: "folderName", label: "Folder" },
      { key: "placeholderName", label: "Expected document" },
      { key: "status", label: "Status" },
      { key: "dueDate", label: "Due date" },
      { key: "assignedToName", label: "Assignee" },
      { key: "fulfilledDocumentName", label: "Document" },
      { key: "signatureStatus", label: "Signature" },
    ],
    [showSiteColumn],
  );

  const readinessTableColumns = useMemo(
    () => readinessFieldOptions.map((f) => f.key),
    [readinessFieldOptions],
  );

  const readinessTableRows = useMemo(() => {
    if (readiness.mode === "placeholder" && placeholderDetails.length) {
      return placeholderDetails.map((r) => ({
        siteName: r.SiteName || "—",
        folderName: r.FolderName || "—",
        placeholderName: r.PlaceholderName || "—",
        status: r.Status || "—",
        dueDate: formatReportDate(r.DueDate),
        assignedToName: r.AssignedToName || "—",
        fulfilledDocumentName: r.FulfilledDocumentName || "—",
        signatureStatus: formatSignatureStatus(r),
      }));
    }
    return (folderCompleteness || []).map((r) => ({
      siteName: "—",
      folderName: r.FolderName || "—",
      placeholderName: "—",
      status: r.HasDocument ? "Complete" : "Empty folder",
      dueDate: "—",
      assignedToName: "—",
      fulfilledDocumentName: "—",
      signatureStatus: "—",
    }));
  }, [readiness.mode, placeholderDetails, folderCompleteness]);

  const milestoneFieldOptions = useMemo(
    () => [
      { key: "milestoneCode", label: "Code" },
      { key: "milestoneName", label: "Milestone" },
      { key: "requiredFulfilled", label: "Fulfilled" },
      { key: "requiredTotal", label: "Required" },
      { key: "requiredPending", label: "Pending" },
      { key: "overdueCount", label: "Overdue" },
      { key: "signedCount", label: "Signed" },
      { key: "signaturePending", label: "Signature pending" },
      { key: "readinessPercent", label: "Readiness %" },
    ],
    [],
  );

  const milestoneTableRows = useMemo(
    () =>
      (milestoneSummary || []).map((r) => ({
        milestoneCode: r.MilestoneCode || "—",
        milestoneName: r.MilestoneName || "—",
        requiredFulfilled: r.RequiredFulfilled ?? 0,
        requiredTotal: r.RequiredTotal ?? 0,
        requiredPending: r.RequiredPending ?? 0,
        overdueCount: r.OverdueCount ?? 0,
        signedCount: `${r.SignedCount ?? 0}/${r.SignatureRequired ?? 0}`,
        signaturePending: r.SignaturePending ?? 0,
        readinessPercent: `${r.ReadinessPercent ?? 0}%`,
        readinessNumeric: Number(r.ReadinessPercent ?? 0),
      })),
    [milestoneSummary],
  );

  const milestoneChartData = useMemo(
    () => ({
      labels: milestoneTableRows.map((r) => r.milestoneCode || r.milestoneName),
      datasets: [
        {
          label: "Readiness %",
          data: milestoneTableRows.map((r) => r.readinessNumeric),
          backgroundColor: "rgba(13, 148, 136, 0.75)",
          borderRadius: 6,
          maxBarThickness: 36,
        },
      ],
    }),
    [milestoneTableRows],
  );

  const milestoneChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => ` ${ctx.parsed.y}% ready`,
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          max: 100,
          ticks: { callback: (v) => `${v}%` },
          grid: { color: "#f1f5f9" },
        },
        x: { grid: { display: false } },
      },
    }),
    [],
  );

  const atRiskFieldOptions = useMemo(
    () => [
      ...(showSiteColumn ? [{ key: "siteName", label: "Site" }] : []),
      { key: "folderName", label: "Folder" },
      { key: "placeholderName", label: "Expected document" },
      { key: "milestoneCode", label: "Milestone" },
      { key: "status", label: "Status" },
      { key: "dueDate", label: "Due date" },
      { key: "riskBucket", label: "Risk bucket" },
    ],
    [showSiteColumn],
  );

  const filteredAtRiskRows = useMemo(() => {
    const bucket = riskFilter?.value;
    return (atRiskDetail || [])
      .filter((r) => !bucket || r.RiskBucket === bucket)
      .map((r) => ({
        siteName: r.SiteName || "—",
        folderName: r.FolderName || "—",
        placeholderName: r.PlaceholderName || "—",
        milestoneCode: r.MilestoneCode || "—",
        status: r.Status || "—",
        dueDate: formatReportDate(r.DueDate),
        riskBucket: r.RiskBucket || "—",
      }));
  }, [atRiskDetail, riskFilter]);

  const siteComparisonFieldOptions = useMemo(
    () => [
      { key: "siteName", label: "Site" },
      { key: "readinessPercent", label: "Readiness %" },
      { key: "requiredFulfilled", label: "Fulfilled" },
      { key: "requiredTotal", label: "Required" },
      { key: "requiredPending", label: "Pending" },
      { key: "overdueCount", label: "Overdue" },
      { key: "signedCount", label: "Signed" },
      { key: "signaturePending", label: "Signature pending" },
    ],
    [],
  );

  const missingFieldOptions = atRiskFieldOptions.filter((f) => f.key !== "riskBucket");
  const missingColumns = missingFieldOptions.map((f) => f.key);

  const missingTableRows = useMemo(
    () =>
      (atRiskDetail || []).map((r) => ({
        siteName: r.SiteName || "—",
        folderName: r.FolderName || "—",
        placeholderName: r.PlaceholderName || "—",
        milestoneCode: r.MilestoneCode || "—",
        status: r.Status || "—",
        dueDate: formatReportDate(r.DueDate),
      })),
    [atRiskDetail],
  );

  const siteComparisonRows = useMemo(
    () =>
      (siteComparison || []).map((r) => ({
        siteName: r.SiteName || "—",
        readinessPercent: `${r.ReadinessPercent ?? 0}%`,
        requiredFulfilled: r.RequiredFulfilled ?? 0,
        requiredTotal: r.RequiredTotal ?? 0,
        requiredPending: r.RequiredPending ?? 0,
        overdueCount: r.OverdueCount ?? 0,
        signedCount: `${r.SignedCount ?? 0}/${r.SignatureRequired ?? 0}`,
        signaturePending: r.SignaturePending ?? 0,
      })),
    [siteComparison],
  );

  const addSheetFromRows = (wb, name, columns, fieldOptions, rows) => {
    const ws = wb.addWorksheet(name.slice(0, 31));
    ws.addRow(columns.map((c) => fieldOptions.find((f) => f.key === c)?.label || c));
    rows.forEach((row) => ws.addRow(columns.map((c) => row[c])));
  };

  const exportExcel = async () => {
    const wb = new ExcelJS.Workbook();
    const summaryWs = wb.addWorksheet("Summary");
    summaryWs.addRow(["Study", study?.label || ""]);
    summaryWs.addRow(["Site", site?.label || "All sites"]);
    summaryWs.addRow(["Category", category?.label || "All"]);
    summaryWs.addRow(["Readiness mode", readiness.mode]);
    summaryWs.addRow(["Readiness %", readiness.percent]);
    if (summary) {
      summaryWs.addRow(["Required pending", summary.RequiredPending ?? 0]);
      summaryWs.addRow(["Overdue", summary.OverdueCount ?? 0]);
      summaryWs.addRow(
        ["Signed", `${summary.SignedCount ?? 0}/${summary.SignatureRequired ?? 0}`],
      );
      summaryWs.addRow(["Signature pending", summary.SignaturePending ?? 0]);
      summaryWs.addRow(["Due within 7 days", summary.DueWithin7Days ?? 0]);
      summaryWs.addRow(["Due within 14 days", summary.DueWithin14Days ?? 0]);
      summaryWs.addRow(["Due within 30 days", summary.DueWithin30Days ?? 0]);
    }

    if (readiness.mode === "placeholder") {
      addSheetFromRows(wb, "Placeholder Details", readinessTableColumns, readinessFieldOptions, readinessTableRows);
      addSheetFromRows(
        wb,
        "Milestone Summary",
        milestoneFieldOptions.map((f) => f.key),
        milestoneFieldOptions,
        milestoneTableRows,
      );
      addSheetFromRows(
        wb,
        "Missing & At Risk",
        atRiskFieldOptions.map((f) => f.key),
        atRiskFieldOptions,
        filteredAtRiskRows,
      );
    }
    if (siteComparisonRows.length > 1) {
      addSheetFromRows(
        wb,
        "Site Comparison",
        siteComparisonFieldOptions.map((f) => f.key),
        siteComparisonFieldOptions,
        siteComparisonRows,
      );
    }

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ISF_Readiness_${study?.label || "study"}.xlsx`.replace(/[^a-zA-Z0-9._-]/g, "_");
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPdf = () => {
    const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "landscape" });
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 12;
    let y = 14;

    doc.setFontSize(14);
    doc.text("eISF Inspection Readiness Report", margin, y);
    y += 8;
    doc.setFontSize(9);
    doc.text(`Study: ${study?.label || "—"}`, margin, y);
    y += 5;
    doc.text(`Site: ${site?.label || "All sites"}`, margin, y);
    y += 5;
    doc.text(`Readiness: ${readiness.percent}% (${readiness.mode})`, margin, y);
    y += 8;

    const writeTable = (title, columns, fieldOptions, rows, maxRows = 25) => {
      if (!rows.length) return;
      if (y > 170) {
        doc.addPage();
        y = 14;
      }
      doc.setFontSize(10);
      doc.setFont(undefined, "bold");
      doc.text(title, margin, y);
      y += 5;
      doc.setFont(undefined, "normal");
      doc.setFontSize(7);
      rows.slice(0, maxRows).forEach((row) => {
        if (y > 190) {
          doc.addPage();
          y = 14;
        }
        const line = columns
          .map((c) => `${fieldOptions.find((f) => f.key === c)?.label || c}: ${row[c] ?? "—"}`)
          .join(" | ");
        const lines = doc.splitTextToSize(line, pageW - margin * 2);
        doc.text(lines, margin, y);
        y += lines.length * 3.5 + 1;
      });
      y += 4;
    };

    if (reportView === "readiness" || reportView === "missing") {
      writeTable(
        reportView === "missing" ? "Missing required" : "Placeholder detail",
        reportView === "missing" ? missingColumns : readinessTableColumns,
        reportView === "missing" ? missingFieldOptions : readinessFieldOptions,
        reportView === "missing" ? missingTableRows : readinessTableRows,
      );
    } else if (reportView === "at-risk") {
      writeTable(
        "Overdue & at-risk",
        atRiskFieldOptions.map((f) => f.key),
        atRiskFieldOptions,
        filteredAtRiskRows,
      );
    } else {
      writeTable(
        "Site comparison",
        siteComparisonFieldOptions.map((f) => f.key),
        siteComparisonFieldOptions,
        siteComparisonRows,
      );
    }

    doc.save(`ISF_Readiness_${study?.label || "study"}.pdf`.replace(/[^a-zA-Z0-9._-]/g, "_"));
  };

  if (loading || !userData) {
    return (
      <div className="d-flex justify-content-center p-5">
        <div className="spinner-border" role="status" />
      </div>
    );
  }

  if (!canView) return <Navigate to="/notification-center" replace />;

  const hasStudy = Boolean(study?.value);
  const reportIsEmpty =
    hasStudy &&
    !reportLoading &&
    !loadError &&
    Number(summary?.TotalPlaceholders ?? 0) === 0 &&
    (readiness.mode !== "folder" || Number(readiness.total ?? 0) === 0);
  const readinessLabel =
    readiness.mode === "placeholder"
      ? `Required docs fulfilled (${phaseLabel || "phase"})`
      : "Folders with documents";

  return (
    <div className="isf-reports-hub">
      <PageBreadcrumb
        items={[
          { label: "Home", path: "/notification-center" },
          { label: "eISF", path: "/isf" },
          { label: "Inspection Readiness" },
        ]}
        actions={[
          { icon: "fas fa-sync-alt", label: "Refresh", onClick: refreshReport },
          { icon: "fas fa-file-pdf", label: "Export PDF", onClick: exportPdf },
          { icon: "fas fa-file-excel", label: "Export Excel", onClick: exportExcel },
        ]}
      />

      {loadError && <div className="isf-reports-hub__alert">{loadError}</div>}

      <div className="isf-reports-hub__shell">
        <aside className="isf-reports-hub__filter-panel">
          <div className="isf-reports-hub__filter-panel-head">
            <h2 className="isf-reports-hub__filter-panel-title">Filters</h2>
          </div>

          <div className="isf-reports-hub__filter-stack">
            <div className="isf-reports-hub__filter-field">
              <label className="isf-reports-hub__field-label">Study</label>
              <Dropdown
                options={studyOptions}
                selected={
                  studiesLoading
                    ? { label: "Loading studies...", value: "__loading__" }
                    : study
                }
                onSelect={setStudy}
                disabled={studiesLoading || !studyOptions.length}
              />
            </div>
            <div className="isf-reports-hub__filter-field">
              <label className="isf-reports-hub__field-label">Category</label>
              <Dropdown options={categoryOptions} selected={category} onSelect={setCategory} />
            </div>
            <div className="isf-reports-hub__filter-field">
              <label className="isf-reports-hub__field-label">Site</label>
              <Dropdown
                options={siteOptions}
                selected={site || ALL_SITES_OPTION}
                onSelect={setSite}
                disabled={!hasStudy || sitesLoading || siteOptions.length <= 1}
              />
            </div>
            {hasStudy && studyGate ? (
              <div className="isf-reports-hub__filter-field">
                <label className="isf-reports-hub__field-label">Study phase</label>
                <IsfPhaseFilter
                  gates={studyGateList}
                  value={selectedGateCodes}
                  onChange={setSelectedGateCodes}
                  disabled={gateLoading}
                />
              </div>
            ) : null}
          </div>

          <div className="isf-reports-hub__filter-divider" />

          <div className="isf-reports-hub__filter-panel-head">
            <h3 className="isf-reports-hub__filter-panel-subtitle">Report type</h3>
          </div>
          <nav className="isf-reports-hub__report-nav" aria-label="Report type">
            {REPORT_VIEWS.map((view) => (
              <button
                key={view.id}
                type="button"
                className={`isf-reports-hub__report-nav-item ${reportView === view.id ? "active" : ""}`}
                onClick={() => setReportView(view.id)}
              >
                {view.label}
              </button>
            ))}
          </nav>

          {reportView === "at-risk" && (
            <>
              <div className="isf-reports-hub__filter-divider" />
              <div className="isf-reports-hub__filter-field">
                <label className="isf-reports-hub__field-label">Risk bucket</label>
                <Dropdown options={RISK_FILTER_OPTIONS} selected={riskFilter} onSelect={setRiskFilter} />
              </div>
            </>
          )}

          {hasStudy && (
            <>
              <div className="isf-reports-hub__filter-divider" />
              {studyGate ? (
                <div className="isf-reports-hub__gate-progress">
                  <StudyGateProgressStrip studyGateProgress={studyGateProgress} />
                </div>
              ) : null}
              <div className="isf-reports-hub__filter-snapshot">
                <div className="isf-reports-hub__snapshot-row">
                  <span>Readiness</span>
                  <strong>{readiness.percent}%</strong>
                </div>
                {readiness.mode === "placeholder" && (
                  <>
                    <div className="isf-reports-hub__snapshot-row">
                      <span>Pending</span>
                      <strong>{summary?.RequiredPending ?? 0}</strong>
                    </div>
                    <div className="isf-reports-hub__snapshot-row isf-reports-hub__snapshot-row--danger">
                      <span>Overdue</span>
                      <strong>{summary?.OverdueCount ?? 0}</strong>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </aside>

        <main className={`isf-reports-hub__main${reportLoading ? " is-loading" : ""}`}>
          {!hasStudy ? (
            <div className="isf-reports-hub__panel isf-reports-hub__empty">
              <h5>Select a study to run reports</h5>
              <p>Choose a study from the filter panel to load readiness metrics.</p>
            </div>
          ) : (
            <>
          {reportLoading && (
            <div className="isf-reports-hub__main-loading" aria-live="polite">
              <div className="spinner-border spinner-border-sm text-secondary" role="status" />
              <span>Updating report…</span>
            </div>
          )}
          {reportIsEmpty && (
            <div className="isf-reports-hub__alert isf-reports-hub__empty-hint">
              No placeholders or folders match the current filters.
              {site?.value
                ? " Try All sites — study-level documents may not be tied to a specific site."
                : " If this study is new, apply an eISF template from the main eISF page first."}
            </div>
          )}
          <div className="isf-reports-hub__kpi-grid">
            <div className="isf-reports-hub__kpi isf-reports-hub__kpi--primary" style={{ "--kpi-accent": "#0d9488" }}>
              <div className="isf-reports-hub__kpi-label">Readiness</div>
              <div className="isf-reports-hub__kpi-value">{readiness.percent}%</div>
            </div>
            {readiness.mode === "placeholder" ? (
              <>
                <div className="isf-reports-hub__kpi" style={{ "--kpi-accent": "#6366f1" }}>
                  <div className="isf-reports-hub__kpi-label">Pending</div>
                  <div className="isf-reports-hub__kpi-value">{summary?.RequiredPending ?? 0}</div>
                </div>
                <div className="isf-reports-hub__kpi isf-reports-hub__kpi--danger" style={{ "--kpi-accent": "#dc2626" }}>
                  <div className="isf-reports-hub__kpi-label">Overdue</div>
                  <div className="isf-reports-hub__kpi-value">{summary?.OverdueCount ?? 0}</div>
                </div>
                <div className="isf-reports-hub__kpi" style={{ "--kpi-accent": "#4A9F7A" }}>
                  <div className="isf-reports-hub__kpi-label">Signed</div>
                  <div className="isf-reports-hub__kpi-value">
                    {summary?.SignedCount ?? 0}/{summary?.SignatureRequired ?? 0}
                  </div>
                </div>
                <div className="isf-reports-hub__kpi isf-reports-hub__kpi--warn" style={{ "--kpi-accent": "#d9a441" }}>
                  <div className="isf-reports-hub__kpi-label">Signature pending</div>
                  <div className="isf-reports-hub__kpi-value">{summary?.SignaturePending ?? 0}</div>
                </div>
                <div className="isf-reports-hub__kpi isf-reports-hub__kpi--warn" style={{ "--kpi-accent": "#d97706" }}>
                  <div className="isf-reports-hub__kpi-label">Due ≤ 7d</div>
                  <div className="isf-reports-hub__kpi-value">{summary?.DueWithin7Days ?? 0}</div>
                </div>
                <div className="isf-reports-hub__kpi" style={{ "--kpi-accent": "#f59e0b" }}>
                  <div className="isf-reports-hub__kpi-label">Due 8–14d</div>
                  <div className="isf-reports-hub__kpi-value">{summary?.DueWithin14Days ?? 0}</div>
                </div>
                <div className="isf-reports-hub__kpi" style={{ "--kpi-accent": "#84cc16" }}>
                  <div className="isf-reports-hub__kpi-label">Due 15–30d</div>
                  <div className="isf-reports-hub__kpi-value">{summary?.DueWithin30Days ?? 0}</div>
                </div>
              </>
            ) : (
              <div className="isf-reports-hub__kpi" style={{ "--kpi-accent": "#0d9488" }}>
                <div className="isf-reports-hub__kpi-label">Folders complete</div>
                <div className="isf-reports-hub__kpi-value">
                  {readiness.complete ?? 0}/{readiness.total ?? 0}
                </div>
              </div>
            )}
          </div>

          {reportView === "readiness" && (
            <div className="isf-reports-hub__layout">
              <div className="isf-reports-hub__panel">
                <h2 className="isf-reports-hub__section-title">Overall readiness</h2>
                <ReadinessRing percent={readiness.percent} label={readinessLabel} />
                {milestoneTableRows.length > 0 && (
                  <>
                    <h2 className="isf-reports-hub__section-title mt-3">By milestone</h2>
                    <div className="isf-reports-hub__chart-wrap">
                      <Bar data={milestoneChartData} options={milestoneChartOptions} />
                    </div>
                  </>
                )}
              </div>
              <div className="isf-reports-hub__tables-col">
                {milestoneTableRows.length > 0 && (
                  <div className="isf-reports-hub__panel isf-reports-hub__table-panel">
                    <h2 className="isf-reports-hub__section-title">Milestone breakdown</h2>
                    <ReportTable
                      rows={milestoneTableRows}
                      fieldOptions={milestoneFieldOptions}
                      defaultColumns={milestoneFieldOptions.map((f) => f.key)}
                    />
                  </div>
                )}
                <div className="isf-reports-hub__panel isf-reports-hub__table-panel">
                  <h2 className="isf-reports-hub__section-title">Document detail</h2>
                  <ReportTable
                    rows={readinessTableRows}
                    fieldOptions={readinessFieldOptions}
                    defaultColumns={readinessTableColumns}
                    onExportExcel={exportExcel}
                    exportLabel="Export Excel"
                  />
                </div>
              </div>
            </div>
          )}

          {reportView === "missing" && (
            <div className="isf-reports-hub__panel isf-reports-hub__table-panel">
              <h2 className="isf-reports-hub__section-title">Missing required documents</h2>
              <ReportTable
                rows={missingTableRows}
                fieldOptions={missingFieldOptions}
                defaultColumns={missingColumns}
                onExportExcel={exportExcel}
                exportLabel="Export Excel"
              />
            </div>
          )}

          {reportView === "at-risk" && (
            <div className="isf-reports-hub__panel isf-reports-hub__table-panel">
              <h2 className="isf-reports-hub__section-title">Overdue & at-risk placeholders</h2>
              <ReportTable
                rows={filteredAtRiskRows}
                fieldOptions={atRiskFieldOptions}
                defaultColumns={atRiskFieldOptions.map((f) => f.key)}
                onExportExcel={exportExcel}
                exportLabel="Export Excel"
              />
            </div>
          )}

          {reportView === "sites" && (
            <div className="isf-reports-hub__panel isf-reports-hub__table-panel">
              <h2 className="isf-reports-hub__section-title">Site comparison</h2>
              {site?.value ? (
                <div className="isf-reports-hub__empty">
                  <p>Select &quot;All sites&quot; in the filter panel to compare readiness across sites.</p>
                </div>
              ) : siteComparisonRows.length === 0 ? (
                <div className="isf-reports-hub__empty">
                  <p>No multi-site data available for this study.</p>
                </div>
              ) : (
                <ReportTable
                  rows={siteComparisonRows}
                  fieldOptions={siteComparisonFieldOptions}
                  defaultColumns={siteComparisonFieldOptions.map((f) => f.key)}
                  onExportExcel={exportExcel}
                  exportLabel="Export Excel"
                />
              )}
            </div>
          )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default IsfReports;
